<template>
  <div class="details">
    <a
      v-if="company.email"
      :href="'mailto:' + company.email"
      class="contact details__button cm-button"
      >{{ I18n.t("contact") }}</a
    >

    <static-map
      v-once
      v-if="company.locations && !company.remote"
      :src="company.google_maps_src"
    ></static-map>

    <div class="details-list sidebar-box">
      <a
        v-if="editable"
        :href="editCompanyPath"
        class="edit-link ss-icon"
        title="Edit company"
        >compose</a
      >
      <dl>
        <template v-if="isOnTheWeb">
          <div class="detail-group">
            <dt class="detail-label">{{ I18n.t("on_the_web") }}</dt>
            <dd class="detail-value">
              <a v-if="company.website" :href="company.website">{{
                websiteHostname
              }}</a>

              <ul class="social-links">
                <li
                  v-for="socialNetwork in socialNetworks"
                  class="social-link"
                  :key="socialNetwork.service"
                >
                  <a :href="socialNetwork.url">
                    <i class="ss-icon ss-social-regular">{{
                      socialNetwork.service
                    }}</i>
                    <span class="social-link__username">{{
                      socialNetwork.username
                    }}</span>
                  </a>
                </li>
              </ul>
            </dd>
          </div>
        </template>

        <template v-if="!company.remote">
          <div v-if="company.locations.length > 1 || company.remote_employees">
            <dt class="detail-label">Locations</dt>
            <dd class="detail-value">
              <ul class="location-list">
                <li v-for="(location, i) in company.locations">
                  <div
                    v-if="i == 0 && company.locations.length > 1"
                    class="hq-tag"
                  >
                    HQ
                  </div>
                  {{ location.address }}
                </li>
                <li v-if="company.remote_employees" class="remote-friendly-tag">
                  <i class="ss-icon">wifi</i>
                  We're remote friendly!
                </li>
              </ul>
            </dd>
          </div>
        </template>

        <template v-if="company.industries.length > 0">
          <div class="detail-group">
            <dt class="detail-label">{{ I18n.t("industries") }}</dt>
            <dd class="detail-value">
              <ul>
                <li
                  class="tag"
                  v-for="(industry, n) in company.industries"
                  v-if="n < 3 || industriesExpanded"
                  :key="industry[0]"
                >
                  <a
                    v-bind:href="getDirectoryPath('industry_id', industry[0])"
                    >{{ industry[1] }}</a
                  >
                </li>
              </ul>
              <a
                class="expand"
                @click.prevent="toggleIndustries"
                v-if="company.industries.length > 3"
              >
                <template v-if="!industriesExpanded">
                  See all {{ company.industries.length }}
                </template>
                <template v-else> See less </template>
              </a>
            </dd>
          </div>
        </template>

        <template v-if="company.expertise.length > 0">
          <div class="detail-group">
            <dt class="detail-label">
              {{ I18n.t("activerecord.attributes.company.skill_tag_ids") }}
            </dt>
            <dd class="detail-value">
              <ul>
                <li
                  class="tag"
                  v-for="(expertise, n) in company.expertise"
                  v-if="n < 3 || expertiseExpanded"
                  :key="expertise[0]"
                >
                  <a
                    v-bind:href="getDirectoryPath('skill_tag_id', expertise[0])"
                    >{{ expertise[1] }}</a
                  >
                </li>
              </ul>
              <a
                class="expand"
                @click.prevent="toggleExpertise"
                v-if="company.expertise.length > 3"
              >
                <template v-if="!expertiseExpanded">
                  See all {{ company.expertise.length }}
                </template>
                <template v-else> See less </template>
              </a>
            </dd>
          </div>
        </template>

        <template v-for="(label, key) in labels" v-if="company[key]">
          <div class="detail-group">
            <dt class="detail-label">{{ label }}</dt>
            <dd class="detail-value">{{ _self[key] || company[key] }}</dd>
          </div>
        </template>
      </dl>
    </div>

    <div v-if="company.global" class="sponsor">
      <div class="global-partner">
        {{ I18n.t("global_partner") }}
      </div>
      <div class="global-partner-link">
        <a href="/partner">{{ I18n.t("what_is_this_question") }}</a>
      </div>
    </div>

    <a
      v-if="editable && isForm"
      class="cm-button details__button view-company"
      :href="viewCompanyPath"
      >{{ I18n.t("view_company") }}</a
    >
    <a
      v-else-if="editable"
      class="cm-button details__button edit-company"
      :href="editCompanyPath"
      >{{ I18n.t("edit_company") }}</a
    >
  </div>
</template>

<script>
import StaticMap from "../components/static-map";

export default {
  props: ["company", "editable", "isForm"],

  data: function () {
    return {
      labels: {
        size: I18n.t("activerecord.attributes.company.size"),
        year_founded: I18n.t("activerecord.attributes.company.year_founded"),
      },
      industriesExpanded: false,
      expertiseExpanded: false,
    };
  },

  computed: {
    websiteHostname() {
      let a = document.createElement("a");

      a.setAttribute("href", this.company.website);

      return a.hostname.replace("www.", "");
    },

    socialNetworks() {
      const networkHash = this.company.social_networks;

      return Object.keys(networkHash)
        .filter((service) => networkHash[service].username)
        .map((service) => ({
          service,
          ...networkHash[service],
        }));
    },

    isOnTheWeb() {
      return this.company.website || this.socialNetworks.length > 0;
    },

    viewCompanyPath() {
      return `/companies/${this.company.slug}`;
    },

    editCompanyPath() {
      return `/companies/${this.company.slug}/edit`;
    },
  },

  methods: {
    getDirectoryPath(filter, id) {
      return `/companies?${filter}=${id}`;
    },

    toggleIndustries() {
      this.industriesExpanded = !this.industriesExpanded;
    },

    toggleExpertise() {
      this.expertiseExpanded = !this.expertiseExpanded;
    },
  },

  components: {
    StaticMap,
  },
};
</script>

<style scoped lang="scss">
@import "../components/edit-button";

.details__button {
  margin-bottom: $gutter / 2;
  margin-top: $gutter / 2;
  width: 100%;
}

.edit-company,
.view-company {
  @include max-width($bp_left_sidebar_stacked) {
    width: auto;
  }
}

.contact {
  @include max-width($bp_left_sidebar_stacked) {
    margin-top: 0;
    width: auto;
  }
}

.company-name {
  @include max-width($bp_left_sidebar_stacked) {
    @include min-width($bp_small_page_title) {
      float: left;
      display: inline-block;
      width: 200px;
      overflow: hidden;
    }
  }
}

.details-list {
  margin: 0;
  margin-top: 0.5rem;
  position: relative;
  @extend %b2;
}

.dl {
  display: flex;
}

.detail-group {
  @include max-width($bp_left_sidebar_stacked) {
    @include min-width($bp_small_page_title) {
      flex: 1;
      margin-right: $gutter;
      display: inline-block;
      vertical-align: top;
    }
  }
}

.edit-link {
  @extend .edit-button;
  position: absolute;
  top: 5px;
  right: 5px;
}

.detail-label {
  @extend %b1e;
  margin: 0.5rem 0;
}

.detail-value {
  margin-left: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.social-links {
  margin-top: 5px;
}

.social-link {
  margin-bottom: $gutter / 4;

  a {
    text-decoration: none;
  }

  i {
    margin-right: 5px;
    position: relative;
    top: 2px;
  }
}

.sponsor {
  text-align: center;
  border-top: 0.5rem solid var(--bg-2);
  margin-top: -0.5rem;

  @include max-width($bp_menu_collapsed) {
    display: none;
  }
}

.global-partner,
.sponsorships {
  background: var(--fg);
  color: var(--bg);
  padding: 1rem 0;
  border-radius: 0 0 $radius $radius;
}

.global-partner,
.sponsorships-count {
  display: block;
}

.sponsorships-label {
  font-family: $body;
}

.global-partner-link {
  margin-top: 0.25rem;

  a {
    @extend %b3;
    color: var(--fg-2);
    text-decoration: none;
  }
}

dl {
  -webkit-margin-before: 0;
  -webkit-margin-after: 0;
}

.tag {
  a {
    background: white;
    color: var(--fg);
  }
}

.expand {
  cursor: pointer;
  font-size: $fs_small;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
}

.hq-tag {
  @extend %b3;
  display: inline-block;
  top: 0;
  float: right;
  line-height: 1rem;
  padding: 0 0.25rem;
  margin-left: 5px;
  background-color: var(--fg-2);
  color: var(--bg);
  border-radius: $radius;
  margin-top: 0.2rem;
}

.remote-friendly-tag {
}

.location-list li {
  padding-bottom: 0.5rem;
}

.social-link {
  width: 95%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>



// WEBPACK FOOTER //
// app/javascript/company/details.vue?8d634b12